import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FolderKanban, FileText, Mail, TrendingUp, Eye, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function DashboardPage() {
  const [stats, setStats] = useState({
    projects: 0,
    posts: 0,
    contacts: 0,
    totalViews: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [projectsRes, postsRes, contactsRes, viewsRes] = await Promise.all([
        supabase.from('projects').select('id', { count: 'exact', head: true }),
        supabase.from('posts').select('id', { count: 'exact', head: true }),
        supabase.from('contacts').select('id', { count: 'exact', head: true }),
        supabase.from('posts').select('views'),
      ]);

      const totalViews = viewsRes.data?.reduce((sum, post) => sum + (post.views || 0), 0) || 0;

      setStats({
        projects: projectsRes.count || 0,
        posts: postsRes.count || 0,
        contacts: contactsRes.count || 0,
        totalViews,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      icon: FolderKanban,
      label: 'Total Projek',
      value: stats.projects,
      color: 'neon-blue',
    },
    {
      icon: FileText,
      label: 'Total Artikel',
      value: stats.posts,
      color: 'neon-green',
    },
    {
      icon: Mail,
      label: 'Pesan Masuk',
      value: stats.contacts,
      color: 'neon-blue',
    },
    {
      icon: Eye,
      label: 'Total Views',
      value: stats.totalViews,
      color: 'neon-green',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6"
      >
        <h1 className="text-3xl font-bold text-white mb-2">
          Selamat Datang, <span className="neon-text-blue">Admin</span>!
        </h1>
        <p className="text-gray-400">
          Kelola website portfolio Anda dengan mudah dari dashboard ini.
        </p>
      </motion.div>

      {/* Stats Grid */}
      {loading ? (
        <div className="text-center text-gray-400">Memuat data...</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="glass-card p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      stat.color === 'neon-blue' ? 'bg-neon-blue/10' : 'bg-neon-green/10'
                    }`}
                  >
                    <Icon
                      className={stat.color === 'neon-blue' ? 'text-neon-blue' : 'text-neon-green'}
                      size={24}
                    />
                  </div>
                  <TrendingUp className="text-green-500" size={18} />
                </div>
                <h3 className="text-3xl font-bold text-white mb-1">{stat.value}</h3>
                <p className="text-gray-400 text-sm">{stat.label}</p>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="glass-card p-6"
      >
        <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <a
            href="/admin/projects"
            className="btn-neon text-center py-4"
          >
            Tambah Projek Baru
          </a>
          <a
            href="/admin/posts"
            className="btn-neon-green text-center py-4"
          >
            Tulis Artikel
          </a>
          <a
            href="/admin/contacts"
            className="btn-neon text-center py-4"
          >
            Lihat Pesan
          </a>
        </div>
      </motion.div>
    </div>
  );
}
